import { normalizeCustomerAddressCountry } from "../../pages/instances/sales/customers/customerAddressValidation";

const GOOGLE_MAPS_SCRIPT_ID = "elite-core-google-maps-js";

let googleMapsScriptPromise = null;

function getGoogleMapsScriptSrc(apiKey) {
  const params = new URLSearchParams({
    key: apiKey,
    libraries: "places",
    loading: "async",
    v: "weekly",
  });
  return `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
}

export function loadGoogleMapsPlacesApi(apiKey) {
  if (!apiKey) {
    return Promise.reject(new Error("Google Maps API key is required."));
  }

  if (typeof window === "undefined" || typeof document === "undefined") {
    return Promise.reject(new Error("Google Maps can only load in the browser."));
  }

  if (window.google?.maps?.importLibrary) {
    return Promise.resolve(window.google);
  }

  if (googleMapsScriptPromise) {
    return googleMapsScriptPromise;
  }

  googleMapsScriptPromise = new Promise((resolve, reject) => {
    const existingScript = document.getElementById(GOOGLE_MAPS_SCRIPT_ID);

    const handleLoad = () => {
      if (window.google?.maps?.importLibrary) {
        resolve(window.google);
        return;
      }
      googleMapsScriptPromise = null;
      reject(new Error("Google Maps loaded, but the Places library is unavailable."));
    };

    const handleError = () => {
      googleMapsScriptPromise = null;
      reject(new Error("Failed to load Google Maps."));
    };

    if (existingScript) {
      existingScript.addEventListener("load", handleLoad, { once: true });
      existingScript.addEventListener("error", handleError, { once: true });
      return;
    }

    const script = document.createElement("script");
    script.id = GOOGLE_MAPS_SCRIPT_ID;
    script.src = getGoogleMapsScriptSrc(apiKey);
    script.async = true;
    script.defer = true;
    script.addEventListener("load", handleLoad, { once: true });
    script.addEventListener("error", handleError, { once: true });
    document.head.appendChild(script);
  });

  return googleMapsScriptPromise;
}

function getAddressComponentMap(addressComponents = []) {
  const map = new Map();

  (Array.isArray(addressComponents) ? addressComponents : []).forEach((component) => {
    const types = Array.isArray(component?.types) ? component.types : [];
    types.forEach((type) => {
      if (!map.has(type)) {
        map.set(type, component);
      }
    });
  });

  return map;
}

function readAddressComponent(componentMap, type, format = "longText") {
  const component = componentMap.get(type);
  if (!component) return "";
  return String(component?.[format] || "").trim();
}

function buildStreetLine(componentMap) {
  const streetNumber = readAddressComponent(componentMap, "street_number", "shortText");
  const route = readAddressComponent(componentMap, "route", "longText");
  return [streetNumber, route].filter(Boolean).join(" ").trim();
}

function buildPostalCode(componentMap) {
  const postalCode = readAddressComponent(componentMap, "postal_code", "longText");
  const postalSuffix = readAddressComponent(componentMap, "postal_code_suffix", "longText");
  if (!postalCode) return "";
  return postalSuffix ? `${postalCode}-${postalSuffix}` : postalCode;
}

function resolveCity(componentMap) {
  return (
    readAddressComponent(componentMap, "locality", "longText") ||
    readAddressComponent(componentMap, "postal_town", "longText") ||
    readAddressComponent(componentMap, "sublocality_level_1", "longText") ||
    readAddressComponent(componentMap, "administrative_area_level_2", "longText") ||
    ""
  );
}

export function mapGooglePlaceToCustomerAddress(place = {}) {
  const componentMap = getAddressComponentMap(place?.addressComponents || []);
  const country = normalizeCustomerAddressCountry(
    readAddressComponent(componentMap, "country", "longText"),
  );

  return {
    street: buildStreetLine(componentMap),
    city: resolveCity(componentMap),
    state: readAddressComponent(componentMap, "administrative_area_level_1", "shortText"),
    postal: buildPostalCode(componentMap),
    country: country || "",
  };
}

export function getGooglePlacesRegionCode(country) {
  switch (normalizeCustomerAddressCountry(country)) {
    case "Canada":
      return "ca";
    case "United States":
      return "us";
    case "Mexico":
      return "mx";
    default:
      return "";
  }
}
